import React from "react";
import {
  Trees,
  Leaf,
  Users,
  BriefcaseBusiness,
} from "lucide-react";

const stats = [
  {
    icon: <Trees size={20} />,
    number: "1,000+",
    text: "hectares restored and under sustainable management",
  },
  {
    icon: <Leaf size={20} />,
    number: "45,000+",
    text: "tCO₂e emissions mitigated through climate-smart practices",
  },
  {
    icon: <Users size={20} />,
    number: "1,500+",
    text: "people trained in climate-smart agriculture and sustainability",
  },
  {
    icon: <BriefcaseBusiness size={20} />,
    number: "1,000+",
    text: "livelihoods created through green economic opportunities",
  },
];

const funding = [
  {
    title: "Climate-smart farming programs",
    value: 27,
  },
  {
    title: "Training for women and youth",
    value: 25,
  },
  {
    title: "Environmental conservation initiatives",
    value: 22,
  },
  {
    title: "Livelihood and enterprise support",
    value: 26,
  },
];

const ImpactDashboards = () => {
  return (
    <section className="bg-[#0B0F0C] text-white py-16 lg:py-28 px-6 md:px-12 lg:px-20 overflow-hidden">
      
      <div className="max-w-7xl mx-auto">
        {/* TOP */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <p className="uppercase tracking-[0.35em] text-xs text-[#7E8A80]">
              Measured Impact You Can Trust
            </p>

            <h2 className="mt-5 text-3xl lg:text-5xl md:text-7xl font-semibold tracking-[-0.05em] leading-none">
              Impact Analytics
            </h2>
          </div>

          <p className="text-[#A1A8A3] md:text-lg max-w-xl leading-relaxed">
            Every contribution is tracked, verified, and linked to real-world
            outcomes.
          </p>
        </div>

        {/* MAIN DASHBOARD */}
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
          {/* LEFT BIG PANEL */}
          <div className="relative rounded-[2.5rem] bg-[#111714] border border-white/5 overflow-hidden p-8 md:p-10 min-h-[650px]">
            {/* grid bg */}
            <div className="absolute inset-0 opacity-[0.04]">
              <div className="w-full h-full bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:70px_70px]" />
            </div>

            {/* top */}
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-[#1D2822] flex items-center justify-center">
                  <Leaf size={22} />
                </div>

                <div>
                  <p className="text-sm text-[#8D9791]">
                    Environmental Impact
                  </p>

                  <p className="text-xs text-[#5E6862] mt-1">
                    Verified outcomes
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-[#162019] px-4 py-2 rounded-full">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />

                <span className="text-xs text-[#D5DDD7]">Live</span>
              </div>
            </div>

            {/* center metric */}
            <div className="relative z-10 mt-20">
              <h1 className="text-[4rem] md:text-[8rem] font-semibold tracking-[-0.08em] leading-none">
                45K
              </h1>

              <div className="flex items-center gap-3 mt-5">
                <div className="px-3 py-1 rounded-full bg-[#1B251F] text-green-400 text-xs">
                  +18.2%
                </div>

                <p className="text-[#A6AEA8] text-sm lg:text-base">
                  tCO₂e emissions mitigated through climate-smart practices
                </p>
              </div>
            </div>

            {/* graph */}
            <div className="absolute bottom-0 left-0 w-full h-[260px]">
              <svg
                viewBox="0 0 900 300"
                className="w-full h-full"
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient
                    id="fillGraph"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#4ADE80" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#4ADE80" stopOpacity="0" />
                  </linearGradient>
                </defs>

                <path
                  d="M0 260 C120 240 180 120 300 140 C400 160 460 40 580 70 C690 100 760 20 900 40"
                  fill="none"
                  stroke="#4ADE80"
                  strokeWidth="5"
                  strokeLinecap="round"
                />

                <path
                  d="M0 260 C120 240 180 120 300 140 C400 160 460 40 580 70 C690 100 760 20 900 40 L900 300 L0 300 Z"
                  fill="url(#fillGraph)"
                />
              </svg>
            </div>
          </div>

          {/* RIGHT */}
          <div className="grid grid-cols-2 gap-6">
            {stats.map((item, index) => (
              <div
                key={index}
                className="relative rounded-[2rem] bg-[#111714] border border-white/5 p-6 overflow-hidden"
              >
                {/* glow */}
                <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-green-500/10 blur-3xl" />

                {/* top */}
                <div className="relative z-10 flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl bg-[#1D2822] flex items-center justify-center text-white">
                    {item.icon}
                  </div>

                  <div className="w-2 h-2 rounded-full bg-green-400" />
                </div>

                {/* content */}
                <div className="relative z-10 mt-10">
                  <h3 className="text-2xl lg:text-4xl font-semibold tracking-[-0.05em]">
                    {item.number}
                  </h3>

                  <p className="mt-4 text-sm leading-relaxed text-[#9DA59F]">
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FUNDING */}
        <div className="mt-24 rounded-[2.5rem] bg-[#111714] border border-white/5 p-8 md:p-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div>
              <p className="uppercase tracking-[0.35em] text-xs text-[#7E8A80]">
                Where Your Funding Goes
              </p>

            <h2 className="mt-5 text-3xl lg:text-5xl md:text-7xl font-semibold tracking-[-0.05em] leading-none">
                Funding Allocation
              </h2>
            </div>

            <p className="text-[#A1A8A3] lg:text-lg max-w-xl leading-relaxed">
              We convert funding into direct, measurable community outcomes -
              not overhead.
            </p>
          </div>

          {/* bars */}
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
            {funding.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[#E5E7EB] lg:text-lg">
                    {item.title}
                  </p>

                  <span className="text-green-400 font-medium">
                    {item.value}%
                  </span>
                </div>

                <div className="h-3 bg-[#1B241F] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-green-400 to-green-300"
                    style={{ width: `${item.value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactDashboards;